import React, { useState, useRef, useContext } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import Bread from "../../common/Bread";
import { useForm } from "react-hook-form";
import { GoLocation } from "react-icons/go";
import { BsTelephone } from "react-icons/bs";
import { MdOutlineEmail } from "react-icons/md";
import "./contact.scss";
import ContactMap from "../../components/ContactMap";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import { LangContext } from "../../context/LangContext";

const ContactUs = () => {
  const { state } = useContext(LangContext);
  const isNepali = state?.np;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const [formLoading, setFormLoading] = useState(false);
  const captchaRef = useRef(null);

  const breadcrumList = {
    image: "",
    title: isNepali ? "सम्पर्क गर्नुहोस" : "Contact Us",
    l1: isNepali ? "गृहपृष्ठ" : "Home",
    l2: isNepali ? "सम्पर्क गर्नुहोस" : "Contact Us",
    l3: "",
  };

  const onSubmit = async (values) => {
    setFormLoading(true);

    // Get reCAPTCHA token
    const token = captchaRef.current.getValue();

    if (!token) {
      toast.error("You must confirm you are not a robot");
      setFormLoading(false);
      return;
    }

    try {
      const { data } = await axios.post(
        "https://admin.icfcbank.com/api/contacts/add_feedback",
        {
          ...values,
          token,
        }
      );

      if (data.status === "SUCCESS") {
        toast.success("Your message was successfully sent.");
        reset();
      } else {
        toast.error(data.status_message || "Something went wrong.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again.");
    } finally {
      setFormLoading(false);
      captchaRef.current.reset(); // Reset captcha every time
    }
  };

  return (
    <Bread data={breadcrumList}>
      <div className="contactPageMainContainer">
        <div className="top-flex-container">
          <div className="flex-item">
            <div className="iconContainer">
              <GoLocation className="icon" aria-hidden="true" focusable="false" />
            </div>
            <div className="contentContainer">
              <h3>{isNepali ? "ठेगाना" : "Address"}</h3>
              <p>
                {isNepali
                  ? "ICFC फाइनान्स लिमिटेड - प्रमुख कार्यालय, थिर्बम साधक-२, भाटभटेनी"
                  : "ICFC Finance Limited - Head Office, Thirbam Sadhak-2, Bhatbhateni"}
              </p>
            </div>
          </div>

          <div className="flex-item">
            <div className="iconContainer">
              <BsTelephone className="icon" aria-hidden="true" focusable="false" />
            </div>
            <div className="contentContainer">
              <h3>{isNepali ? "फोन" : "Phone"}</h3>
              <p>
                <a href="tel:+97714525292" aria-label={isNepali ? "फोन ०१-४५२५२९२" : "Call 01-4525292"}>
                  01-4525292
                </a>
              </p>
            </div>
          </div>

          <div className="flex-item">
            <div className="iconContainer">
              <MdOutlineEmail className="icon" aria-hidden="true" focusable="false" />
            </div>
            <div className="contentContainer">
              <h3>{isNepali ? "इमेल" : "Email"}</h3>
              <p>
                <a href="mailto:info@icfcbank.com" aria-label={isNepali ? "इमेल info@icfcbank.com" : "Email info@icfcbank.com"}>
                  info@icfcbank.com
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="bottom-flex-container">
          <div className="formContainer">
            <h2>{isNepali ? "सम्पर्क गर्नुहोस" : "Contact Us"}</h2>
            <form className="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="contactFormInputContainer">
                <label htmlFor="contact-fullname">
                  {isNepali ? "पूरा नाम" : "Full Name"}
                </label>
                <input
                  id="contact-fullname"
                  type="text"
                  autoComplete="name"
                  aria-required="true"
                  aria-invalid={errors.fullname ? "true" : "false"}
                  aria-describedby={errors.fullname ? "contact-fullname-err" : undefined}
                  {...register("fullname", {
                    required: isNepali ? "पूरा नाम आवश्यक छ" : "Full Name is Required",
                  })}
                  placeholder={isNepali ? "पूरा नाम" : "Full Name"}
                />
                {errors.fullname && (
                  <span id="contact-fullname-err" role="alert">
                    {errors.fullname.message}
                  </span>
                )}
              </div>

              <div className="contactFormInputContainer">
                <label htmlFor="contact-email">
                  {isNepali ? "इमेल" : "Email"}
                </label>
                <input
                  id="contact-email"
                  type="email"
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={errors.email ? "true" : "false"}
                  aria-describedby={errors.email ? "contact-email-err" : undefined}
                  {...register("email", {
                    required: isNepali ? "इमेल आवश्यक छ" : "Email is Required",
                    pattern: {
                      value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i,
                      message: isNepali ? "अवैध इमेल ठेगाना" : "Invalid email address",
                    },
                  })}
                  placeholder={isNepali ? "इमेल" : "Email"}
                />
                {errors.email && (
                  <span id="contact-email-err" role="alert">
                    {errors.email.message}
                  </span>
                )}
              </div>

              <div className="contactFormInputContainer">
                <label htmlFor="contact-phone">
                  {isNepali ? "मोबाइल नम्बर" : "Mobile Number"}
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  autoComplete="tel"
                  aria-required="true"
                  aria-invalid={errors.phone ? "true" : "false"}
                  aria-describedby={errors.phone ? "contact-phone-err" : undefined}
                  {...register("phone", {
                    required: isNepali ? "फोन आवश्यक छ" : "Phone is Required",
                    pattern: {
                      value: /^[\+]?[0-9\s\-().]{7,20}$/,
                      message: isNepali ? "अवैध फोन नम्बर" : "Invalid phone number",
                    },
                  })}
                  placeholder={isNepali ? "मोबाइल नम्बर" : "Mobile Number"}
                />
                {errors.phone && (
                  <span id="contact-phone-err" role="alert">
                    {errors.phone.message}
                  </span>
                )}
              </div>

              <div className="contactFormInputContainer">
                <label htmlFor="contact-address">
                  {isNepali ? "ठेगाना" : "Address"}
                </label>
                <input
                  id="contact-address"
                  type="text"
                  autoComplete="street-address"
                  aria-required="true"
                  aria-invalid={errors.address ? "true" : "false"}
                  aria-describedby={errors.address ? "contact-address-err" : undefined}
                  {...register("address", {
                    required: isNepali ? "ठेगाना आवश्यक छ" : "Address is Required",
                  })}
                  placeholder={isNepali ? "ठेगाना" : "Address"}
                />
                {errors.address && (
                  <span id="contact-address-err" role="alert">
                    {errors.address.message}
                  </span>
                )}
              </div>

              <div className="contactFormInputContainer">
                <label htmlFor="contact-message">
                  {isNepali ? "तपाईंको सन्देश" : "Your Message"}
                </label>
                <textarea
                  id="contact-message"
                  rows="5"
                  aria-required="true"
                  aria-invalid={errors.message ? "true" : "false"}
                  aria-describedby={errors.message ? "contact-message-err" : undefined}
                  {...register("message", {
                    required: isNepali ? "सन्देश आवश्यक छ" : "Message is Required",
                    minLength: { value: 5, message: isNepali ? "न्यूनतम ५ अक्षर" : "Minimum 5 characters" },
                    maxLength: { value: 5000, message: isNepali ? "अधिकतम ५००० अक्षर" : "Max 5000 characters" },
                  })}
                  placeholder={isNepali ? "सन्देश" : "Message"}
                />
                {errors.message && (
                  <span id="contact-message-err" role="alert">
                    {errors.message.message}
                  </span>
                )}
              </div>

              <div className="captchaContainer" aria-label={isNepali ? "CAPTCHA प्रमाणीकरण" : "CAPTCHA verification"}>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_SITE_KEY}
                  ref={captchaRef}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || formLoading}
                aria-busy={formLoading}
                className="contactFormResetButton contactSubmitButton"
              >
                {formLoading && (
                  <ClipLoader size={20} color="#fff" aria-hidden="true" focusable="false" />
                )}
                {isNepali ? "पेश गर्नुहोस्" : "Submit"}
              </button>
            </form>
          </div>

          <div className="mapContainer">
            <ContactMap />
          </div>
        </div>
      </div>
    </Bread>
  );
};

export default ContactUs;
